import { type Cadre, LIMITES_ENVOI, natureFichier } from '@admin-pmvb/core'
import { Link, useNavigate } from '@tanstack/react-router'
import { useEffect, useEffectEvent, useRef, useState } from 'react'
import { drainer, relireFile } from '../pwa/etat-file'
import {
  type ElementFile,
  formulaireTranche,
  mettreEnFile,
  nouvelIdentifiant,
} from '../pwa/file-envoi'
import { marquerOccupee } from '../pwa/occupation'
import { ChampIndication } from '../composants/Dictee'
import { RecadragePage } from '../composants/RecadragePage'
import { EntetePage } from '../composants/Ui'
import { analyserPhoto, apercuRedresse } from '../recadrage-photo'

// Photographier un document : une ou plusieurs pages (appareil photo, galerie ou fichiers),
// réordonnées et vérifiées avant l'envoi. Les bords de chaque page sont cherchés sur le
// téléphone et proposés (écran de recadrage après chaque photo prise, « Recadrer » sur chaque
// page) ; la vignette montre la page redressée. Les photos partent en pleine résolution,
// jamais compressées ni redimensionnées sur le téléphone, avec les coins retenus : le serveur
// redresse la page. Elles sont écrites dans la file d'envoi du téléphone AVANT l'envoi, qui
// reprend seul après une coupure.

type Page = {
  id: string
  fichier: File
  apercu: string | null
  /** Bords de la page : cherchés, trouvés ou non, ou illisibles ici (PDF, HEIC hors Safari). */
  recadrage: 'recherche' | 'pret' | 'indisponible'
  /** Bords trouvés par la recherche. */
  proposition: Cadre | null
  /** Coins retenus : le serveur redresse la page ; null : la photo entière. */
  cadre: Cadre | null
  /** Aperçu de la page redressée. */
  apercuCadre: string | null
}

const MO = 1024 * 1024

/** Photos prises d'un toucher depuis l'accueil : reprises par la page à son ouverture. */
let photosConfiees: File[] = []

export function confierPhotos(fichiers: FileList) {
  photosConfiees = Array.from(fichiers)
}

export function Photo() {
  const navigate = useNavigate()
  const [pages, setPages] = useState<Page[]>([])
  const [indication, setIndication] = useState('')
  const [messages, setMessages] = useState<string[]>([])
  const [enCours, setEnCours] = useState(false)
  const [edition, setEdition] = useState<string | null>(null)
  const appareil = useRef<HTMLInputElement>(null)
  const galerie = useRef<HTMLInputElement>(null)
  const apercus = useRef(new Set<string>())
  /** Aperçu redressé de chaque page, révoqué quand il est remplacé. */
  const apercusCadre = useRef(new Map<string, string>())
  /** Version des coins de chaque page : un aperçu en retard ne remplace pas le suivant. */
  const versions = useRef(new Map<string, number>())
  /** Recherches des bords, une à la fois (décoder dix photos ensemble saturerait le téléphone). */
  const recherches = useRef<Promise<void>>(Promise.resolve())

  // Prises à l'instant, comme « Prendre une photo » : le recadrage s'ouvre.
  const reprendrePhotos = useEffectEvent((fichiers: File[]) => ajouter(fichiers, true))
  // Une fois, à l'ouverture : les photos prises depuis l'accueil deviennent les premières pages.
  useEffect(() => {
    if (photosConfiees.length === 0) return
    const fichiers = photosConfiees
    photosConfiees = []
    reprendrePhotos(fichiers)
  }, [])

  // Pages choisies et pas encore envoyées : une mise à jour de l'appli attend.
  useEffect(() => {
    marquerOccupee('selection', pages.length > 0)
  }, [pages.length])
  // Aperçus libérés au démontage, un tour plus tard : le démontage simulé du mode strict
  // (développement) est suivi aussitôt d'un remontage, qui l'annule ; sinon la photo reprise
  // de l'accueil à l'ouverture perdrait son aperçu.
  const liberation = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  useEffect(() => {
    clearTimeout(liberation.current)
    const urls = apercus.current
    const vignettes = apercusCadre.current
    return () => {
      marquerOccupee('selection', false)
      liberation.current = setTimeout(() => {
        for (const u of urls) URL.revokeObjectURL(u)
        for (const u of vignettes.values()) URL.revokeObjectURL(u)
      }, 0)
    }
  }, [])

  /** Remplace l'aperçu redressé d'une page (l'ancien est révoqué). */
  function poserApercuCadre(id: string, url: string | null) {
    const ancien = apercusCadre.current.get(id)
    if (ancien) URL.revokeObjectURL(ancien)
    if (url) apercusCadre.current.set(id, url)
    else apercusCadre.current.delete(id)
    setPages((p) => p.map((x) => (x.id === id ? { ...x, apercuCadre: url } : x)))
  }

  function rechercherBords(id: string, apercu: string) {
    recherches.current = recherches.current.then(async () => {
      const avant = versions.current.get(id) ?? 0
      const r = await analyserPhoto(apercu)
      // Page retirée entre-temps (son aperçu est déjà révoqué), ou coins déjà choisis à la main.
      const choisi = versions.current.get(id) !== avant
      if (r?.apercu && (choisi || !apercus.current.has(apercu))) URL.revokeObjectURL(r.apercu)
      if (!apercus.current.has(apercu)) return
      setPages((p) =>
        p.map((x) =>
          x.id === id
            ? {
                ...x,
                recadrage: r ? 'pret' : 'indisponible',
                proposition: r?.proposition ?? null,
                ...(!choisi && { cadre: r?.proposition ?? null }),
              }
            : x,
        ),
      )
      if (!choisi) poserApercuCadre(id, r?.apercu ?? null)
      // Illisible ici : l'écran de recadrage ne peut rien montrer.
      if (!r) setEdition((e) => (e === id ? null : e))
    })
  }

  async function validerCadre(page: Page, cadre: Cadre | null) {
    setEdition(null)
    const version = (versions.current.get(page.id) ?? 0) + 1
    versions.current.set(page.id, version)
    setPages((p) => p.map((x) => (x.id === page.id ? { ...x, cadre } : x)))
    const url = cadre && page.apercu ? await apercuRedresse(page.apercu, cadre) : null
    if (versions.current.get(page.id) !== version || !apercus.current.has(page.apercu ?? '')) {
      if (url) URL.revokeObjectURL(url)
      return
    }
    poserApercuCadre(page.id, url)
  }

  function ajouter(liste: FileList | File[] | null, depuisAppareil = false) {
    if (!liste) return
    const refus: string[] = []
    const nouvelles: Page[] = []
    for (const fichier of Array.from(liste)) {
      const nature = natureFichier(fichier.name, fichier.type)
      if (!nature) {
        refus.push(`${fichier.name || 'fichier'} : format refusé (photo ou PDF seulement).`)
        continue
      }
      if (fichier.size > LIMITES_ENVOI.octetsParFichier) {
        refus.push(`${fichier.name} : plus de ${LIMITES_ENVOI.octetsParFichier / MO} Mo.`)
        continue
      }
      let apercu: string | null = null
      if (nature !== 'pdf') {
        apercu = URL.createObjectURL(fichier)
        apercus.current.add(apercu)
      }
      nouvelles.push({
        id: nouvelIdentifiant(),
        fichier,
        apercu,
        recadrage: apercu ? 'recherche' : 'indisponible',
        proposition: null,
        cadre: null,
        apercuCadre: null,
      })
    }
    const place = LIMITES_ENVOI.fichiersParEnvoi - pages.length
    if (nouvelles.length > place)
      refus.push(`${LIMITES_ENVOI.fichiersParEnvoi} pages au plus par document.`)
    const gardees = nouvelles.slice(0, Math.max(0, place))
    for (const p of nouvelles.slice(gardees.length))
      if (p.apercu) {
        URL.revokeObjectURL(p.apercu)
        apercus.current.delete(p.apercu)
      }
    setPages((p) => [...p, ...gardees])
    setMessages(refus)
    for (const p of gardees) if (p.apercu) rechercherBords(p.id, p.apercu)
    // Une photo prise à l'instant : son recadrage s'ouvre aussitôt.
    const prise = gardees[0]
    if (depuisAppareil && gardees.length === 1 && prise?.apercu) setEdition(prise.id)
  }

  function retirer(i: number) {
    const page = pages[i]
    if (!page) return
    if (page.apercu) {
      URL.revokeObjectURL(page.apercu)
      apercus.current.delete(page.apercu)
    }
    poserApercuCadre(page.id, null)
    setPages((p) => p.filter((x) => x.id !== page.id))
  }

  function deplacer(i: number, sens: -1 | 1) {
    setPages((p) => {
      const j = i + sens
      if (j < 0 || j >= p.length) return p
      const copie = [...p]
      ;[copie[i], copie[j]] = [copie[j]!, copie[i]!]
      return copie
    })
  }

  async function envoyer() {
    if (pages.length === 0 || enCours) return
    setEnCours(true)
    const envoiId = nouvelIdentifiant()
    const base = Date.now()
    const indicationEnvoi = indication.trim().slice(0, 500)
    const elements: ElementFile[] = pages.map((p, i) => ({
      id: nouvelIdentifiant(),
      envoiId,
      nbFichiers: pages.length,
      rang: i,
      blob: p.fichier,
      nom: p.fichier.name || `page-${i + 1}.jpg`,
      type: p.fichier.type,
      taille: p.fichier.size,
      creeLe: base + i,
      tentatives: 0,
      derniereErreur: null,
      dernierEssai: null,
      ...(indicationEnvoi && { indication: indicationEnvoi }),
      ...(p.cadre && { cadre: p.cadre }),
    }))
    try {
      // Écrites sur le téléphone AVANT l'envoi : rien ne se perd si l'appli est fermée.
      await mettreEnFile(elements)
    } catch {
      // Sans IndexedDB (navigation privée) : envoi direct, sans reprise automatique.
      try {
        const r = await fetch('/api/envois', {
          method: 'POST',
          credentials: 'same-origin',
          body: formulaireTranche(elements),
        })
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
      } catch {
        setMessages([
          'Envoi impossible et ce navigateur ne peut pas garder les photos : réessayez avec du réseau (ou hors navigation privée).',
        ])
        setEnCours(false)
        return
      }
    }
    for (const p of pages) if (p.apercu) URL.revokeObjectURL(p.apercu)
    apercus.current.clear()
    for (const u of apercusCadre.current.values()) URL.revokeObjectURL(u)
    apercusCadre.current.clear()
    setPages([])
    setIndication('')
    marquerOccupee('selection', false)
    await relireFile()
    void drainer({ prioritaire: envoiId })
    await navigate({ to: '/envois' })
  }

  const enRecherche = pages.some((p) => p.recadrage === 'recherche')
  const enEdition = pages.find((p) => p.id === edition && p.apercu)

  return (
    <main className="photo">
      <EntetePage surtitre="Courrier" titre="Photographier un document" />
      <p className="discret">
        Une photo par page, en pleine lumière, le document à plat sur un fond plus sombre que lui :
        ses bords sont trouvés et la page redressée. Plusieurs pages font un seul PDF, dans l’ordre
        ci-dessous.
      </p>

      <input
        ref={appareil}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={(e) => {
          ajouter(e.target.files, true)
          e.target.value = ''
        }}
      />
      <input
        ref={galerie}
        type="file"
        accept="image/*,application/pdf,.heic,.heif"
        multiple
        hidden
        onChange={(e) => {
          ajouter(e.target.files)
          e.target.value = ''
        }}
      />

      <div className="photo-actions">
        <button
          type="button"
          className="bouton bouton-grand"
          onClick={() => appareil.current?.click()}
        >
          {pages.length === 0 ? 'Prendre une photo' : 'Ajouter une page'}
        </button>
        <button type="button" className="bouton-discret" onClick={() => galerie.current?.click()}>
          Choisir dans la galerie ou les fichiers
        </button>
      </div>

      {messages.map((m) => (
        <p key={m} className="alerte">
          {m}
        </p>
      ))}

      {pages.length > 0 && (
        <section className="carte section" aria-label="Pages à envoyer">
          <div className="section-titre">
            <h2>
              {pages.length} page{pages.length > 1 ? 's' : ''}
            </h2>
          </div>
          <ol className="pages-photo">
            {pages.map((p, i) => (
              <li key={p.id} className="page-photo">
                {p.apercu ? (
                  <img
                    src={p.apercuCadre ?? p.apercu}
                    alt={`Page ${i + 1}`}
                    className={p.apercuCadre ? 'page-photo-redressee' : undefined}
                  />
                ) : (
                  <span className="page-photo-pdf" aria-label={`Page ${i + 1}, PDF`}>
                    PDF
                  </span>
                )}
                <div className="page-photo-infos">
                  <strong>Page {i + 1}</strong>
                  <span className="discret">
                    {p.fichier.name || 'photo'} ·{' '}
                    {(p.fichier.size / MO).toFixed(1).replace('.', ',')} Mo
                  </span>
                  {p.recadrage !== 'indisponible' && (
                    <span className="discret">
                      {p.recadrage === 'recherche'
                        ? 'Recherche des bords…'
                        : p.cadre
                          ? 'Page recadrée et redressée'
                          : 'Photo entière'}
                    </span>
                  )}
                  <div className="page-photo-boutons">
                    {p.apercu && p.recadrage === 'pret' && (
                      <button
                        type="button"
                        className="bouton-discret"
                        onClick={() => setEdition(p.id)}
                        aria-label={`Recadrer la page ${i + 1}`}
                      >
                        Recadrer
                      </button>
                    )}
                    <button
                      type="button"
                      className="bouton-discret"
                      disabled={i === 0}
                      onClick={() => deplacer(i, -1)}
                      aria-label={`Monter la page ${i + 1}`}
                    >
                      Monter
                    </button>
                    <button
                      type="button"
                      className="bouton-discret"
                      disabled={i === pages.length - 1}
                      onClick={() => deplacer(i, 1)}
                      aria-label={`Descendre la page ${i + 1}`}
                    >
                      Descendre
                    </button>
                    <button
                      type="button"
                      className="bouton-discret"
                      onClick={() => retirer(i)}
                      aria-label={`Retirer la page ${i + 1}`}
                    >
                      Retirer
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ol>
          <ChampIndication id="indication-photo" valeur={indication} onChange={setIndication} />
          <div className="boutons">
            <button
              type="button"
              className="bouton bouton-grand"
              disabled={enCours || enRecherche}
              onClick={envoyer}
            >
              {enCours ? 'Enregistrement…' : enRecherche ? 'Recherche des bords…' : 'Envoyer'}
            </button>
          </div>
        </section>
      )}

      <p className="discret">
        <Link to="/envois">Mes envois</Link> : suivre les documents envoyés.
      </p>

      {enEdition?.apercu && (
        <RecadragePage
          key={`${enEdition.id}:${enEdition.recadrage}`}
          photo={enEdition.apercu}
          cadre={enEdition.cadre}
          proposition={enEdition.proposition}
          recherche={enEdition.recadrage === 'recherche'}
          onValider={(cadre) => void validerCadre(enEdition, cadre)}
          onFermer={() => setEdition(null)}
        />
      )}
    </main>
  )
}
